export type PackageGateMode = "allow" | "force";
export type PackageGateDecisionSource =
  | "not_applicable"
  | "network"
  | "verified_cache"
  | "fail_open";

export interface PackageStatusSnapshot {
  schemaVersion: 1;
  revision: number;
  generatedAt: string;
  expiresAt: string;
  packages: Record<string, PackageGateMode>;
}

export interface PackageGateDecision {
  mode: PackageGateMode;
  sourceKey: string | null;
  decisionSource: PackageGateDecisionSource;
  revision: number | null;
  reason: string;
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type FetchResponseLike = {
  ok: boolean;
  headers: Pick<Headers, "get">;
  json: () => Promise<unknown>;
};

type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<FetchResponseLike>;

const CACHE_KEY = "webhall_package_status_snapshot_v1";
const REQUEST_TIMEOUT_MS = 2_000;
const MAX_SNAPSHOT_TTL_MS = 5 * 60 * 1000;
const EXPECTED_SOURCE_KEYS = Array.from(
  { length: 23 },
  (_, index) => `apk${String(index + 1).padStart(2, "0")}`,
);

export function resolveOnlinePackageSourceKey(hostname: string): string | null {
  const normalizedHost = hostname.trim().toLowerCase();
  if (normalizedHost === "apk.7betin.com") return "apk01";

  const match = normalizedHost.match(/^apk(0[1-9]|1[0-9]|2[0-3])\./);
  return match ? `apk${match[1]}` : null;
}

export function validatePackageStatusSnapshot(
  input: unknown,
  nowMs: number,
): PackageStatusSnapshot | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const value = input as Partial<PackageStatusSnapshot>;
  if (
    value.schemaVersion !== 1 ||
    !Number.isSafeInteger(value.revision) ||
    (value.revision ?? -1) < 0 ||
    typeof value.generatedAt !== "string" ||
    typeof value.expiresAt !== "string" ||
    !value.packages ||
    typeof value.packages !== "object" ||
    Array.isArray(value.packages)
  ) {
    return null;
  }

  const generatedAtMs = Date.parse(value.generatedAt);
  const expiresAtMs = Date.parse(value.expiresAt);
  if (
    !Number.isFinite(generatedAtMs) ||
    !Number.isFinite(expiresAtMs) ||
    expiresAtMs <= nowMs ||
    expiresAtMs <= generatedAtMs ||
    expiresAtMs - generatedAtMs > MAX_SNAPSHOT_TTL_MS
  ) {
    return null;
  }

  const packageKeys = Object.keys(value.packages).sort();
  if (
    packageKeys.length !== EXPECTED_SOURCE_KEYS.length ||
    !packageKeys.every((key, index) => key === EXPECTED_SOURCE_KEYS[index])
  ) {
    return null;
  }
  if (
    EXPECTED_SOURCE_KEYS.some(
      (key) => value.packages?.[key] !== "allow" && value.packages?.[key] !== "force",
    )
  ) {
    return null;
  }

  return {
    schemaVersion: 1,
    revision: value.revision as number,
    generatedAt: value.generatedAt,
    expiresAt: value.expiresAt,
    packages: Object.fromEntries(
      EXPECTED_SOURCE_KEYS.map((key) => [key, value.packages?.[key]]),
    ) as Record<string, PackageGateMode>,
  };
}

function readVerifiedCache(
  storage: StorageLike | null,
  nowMs: number,
): PackageStatusSnapshot | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(CACHE_KEY);
    if (!raw) return null;
    const snapshot = validatePackageStatusSnapshot(JSON.parse(raw), nowMs);
    if (!snapshot) storage.removeItem(CACHE_KEY);
    return snapshot;
  } catch {
    storage.removeItem(CACHE_KEY);
    return null;
  }
}

function storeVerifiedCache(
  storage: StorageLike | null,
  snapshot: PackageStatusSnapshot,
): void {
  if (!storage) return;
  try {
    storage.setItem(CACHE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage is optional. A storage failure must never block the lobby.
  }
}

function decisionFromSnapshot(
  snapshot: PackageStatusSnapshot,
  sourceKey: string,
  decisionSource: "network" | "verified_cache",
): PackageGateDecision {
  return {
    mode: snapshot.packages[sourceKey] === "force" ? "force" : "allow",
    sourceKey,
    decisionSource,
    revision: snapshot.revision,
    reason: decisionSource === "network" ? "snapshot_network" : "snapshot_cache",
  };
}

export async function resolvePackageGateDecision(input: {
  hostname: string;
  origin: string;
  isOfficialOfflineApk: boolean;
  statusUrl?: string;
  fetchImpl?: FetchLike;
  sessionStorage?: StorageLike | null;
  now?: () => number;
  timeoutMs?: number;
}): Promise<PackageGateDecision> {
  const sourceKey = resolveOnlinePackageSourceKey(input.hostname);
  if (input.isOfficialOfflineApk || !sourceKey) {
    return {
      mode: "allow",
      sourceKey,
      decisionSource: "not_applicable",
      revision: null,
      reason: input.isOfficialOfflineApk ? "offline_apk" : "unknown_source",
    };
  }

  const now = input.now ?? Date.now;
  const nowMs = now();
  const storage = input.sessionStorage ?? null;
  const cached = readVerifiedCache(storage, nowMs);
  const configuredUrl = input.statusUrl?.trim() || "/public/status.json";
  let statusUrl: URL;
  try {
    statusUrl = new URL(configuredUrl, input.origin);
  } catch {
    return cached
      ? decisionFromSnapshot(cached, sourceKey, "verified_cache")
      : {
          mode: "allow",
          sourceKey,
          decisionSource: "fail_open",
          revision: null,
          reason: "invalid_status_url",
        };
  }

  const isLocalDevelopment =
    statusUrl.hostname === "localhost" || statusUrl.hostname === "127.0.0.1";
  if (statusUrl.protocol !== "https:" && !isLocalDevelopment) {
    return cached
      ? decisionFromSnapshot(cached, sourceKey, "verified_cache")
      : {
          mode: "allow",
          sourceKey,
          decisionSource: "fail_open",
          revision: null,
          reason: "insecure_status_url",
        };
  }

  const fetchImpl = input.fetchImpl ?? (fetch as unknown as FetchLike);
  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(),
    input.timeoutMs ?? REQUEST_TIMEOUT_MS,
  );
  try {
    const response = await fetchImpl(statusUrl, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    const contentType = response.headers.get("content-type") || "";
    if (!response.ok || !contentType.toLowerCase().includes("application/json")) {
      throw new Error("invalid_response");
    }
    const snapshot = validatePackageStatusSnapshot(await response.json(), now());
    if (!snapshot) throw new Error("invalid_snapshot");

    if (cached && cached.revision > snapshot.revision) {
      return decisionFromSnapshot(cached, sourceKey, "verified_cache");
    }
    storeVerifiedCache(storage, snapshot);
    return decisionFromSnapshot(snapshot, sourceKey, "network");
  } catch {
    const fallback = readVerifiedCache(storage, now());
    return fallback
      ? decisionFromSnapshot(fallback, sourceKey, "verified_cache")
      : {
          mode: "allow",
          sourceKey,
          decisionSource: "fail_open",
          revision: null,
          reason: "status_unavailable",
        };
  } finally {
    window.clearTimeout(timer);
  }
}
